import { GraphQL, useRpiGraphqlRequest } from "@rpi/openapi-api";
import { filterByMatchingAttributes } from "../../hooks/useStandardProducts";
import { ATTRIBUTES } from "./attributeHelpers";
import { ChoiceStep } from "./ChoiceStep";
import { StepProps } from "./ProductCreateNavigator";
import { getDisplayName, getProductImage } from "./attributeValueMappings";
import { ProductImage } from "./ProductImage";
import { TextCard } from "./TextCard";
import { useState } from "react";

export function PaperTypeStep(props: StepProps) {
  const [isCustomerProductCheckLoading, setIsCustomerProductCheckLoading] = useState(false);

  // Resolve the product from the full selection (including the chosen paper) for the duplicate check.
  const selectedProducts = filterByMatchingAttributes(props.standardProducts, props.state.attributes);
  const openapiProductId = selectedProducts.length === 1 ? selectedProducts[0].id : undefined;

  const request = useRpiGraphqlRequest<GraphQL.CustomerProductsQuery, GraphQL.CustomerProductsQueryVariables>(
    GraphQL.CustomerProductsDocument
  );
  const validateOpenapiSkuUnused = async () => {
    if (!openapiProductId) {
      return;
    }

    try {
      setIsCustomerProductCheckLoading(true);
      // Check that the customer hasn't already used this openapi product before advancing to the final step
      // This provides a nicer UX than the error they would get on trying to save the product
      const data = await request({
        limit: 1,
        offset: 0,
        search: {
          inputs: [
            {
              field: GraphQL.CustomerProductSearchField.OpenapiProductId,
              condition: GraphQL.SearchCondition.Equals,
              firstValue: openapiProductId?.toString(),
            },
          ],
        },
      });
      if (data.customerProducts.data.length > 0) {
        return (
          <>
            You already have a product with this exact combination of Cover, Finish, Orientation, Size, Bind, and Paper.
            <br />
            <br />
            Please go back to the previous steps to change one of these selections.
          </>
        );
      }
    } finally {
      setIsCustomerProductCheckLoading(false);
    }
  };

  return (
    <ChoiceStep
      {...props}
      restrictedValues="disable"
      renderCard={(combination, id, { disabled, renderTooltip }) => {
        const weightName = getDisplayName(ATTRIBUTES.TEXT_STOCK, String(combination[ATTRIBUTES.TEXT_STOCK]));
        const styleName = getDisplayName(ATTRIBUTES.STOCK_STYLE, String(combination[ATTRIBUTES.STOCK_STYLE]));
        if (!weightName || !styleName) return null;
        return (
          <TextCard
            key={id}
            value={id}
            displayName={`${weightName} ${styleName}`}
            disabled={disabled}
            renderTooltip={renderTooltip}
          />
        );
      }}
      renderImage={(combination) => {
        const weightName = getDisplayName(ATTRIBUTES.TEXT_STOCK, String(combination[ATTRIBUTES.TEXT_STOCK]));
        const styleName = getDisplayName(ATTRIBUTES.STOCK_STYLE, String(combination[ATTRIBUTES.STOCK_STYLE]));
        const image = getProductImage(ATTRIBUTES.STOCK_STYLE, String(combination[ATTRIBUTES.STOCK_STYLE]));
        return image && weightName && styleName ? (
          <ProductImage displayName={`${weightName} ${styleName}`} image={image} />
        ) : null;
      }}
      flexDirection="column"
      onAdditionalValidation={validateOpenapiSkuUnused}
      isLoading={isCustomerProductCheckLoading}
    />
  );
}

// The wizard card annotates the shared interior-color name with its print ratio, per Figma Step 7.1
// (e.g. "Full Color (4/4)"); the shared mapping stays plain ("Full Color") for the product details view.
const INTERIOR_COLOR_RATIOS = new Map<string, string>([
  ["44", "4/4"],
  ["11", "1/1"],
]);

// Per Figma tooltip rules (node 15019:83938), the disabled Black & White card gets an addendum after the
// shared unavailable-option sentence explaining that a black-and-white design is still printed CMYK.
const BLACK_AND_WHITE_ADDENDUM =
  "If your design is black and white, it will remain black and white, but will be printed using 4-color CMYK (4/4).";

export function InteriorColorStep(props: StepProps) {
  return (
    <ChoiceStep
      {...props}
      restrictedValues="disable"
      getTooltipSuffix={(combination) => {
        const value = String(combination[ATTRIBUTES.TEXT_PRINT_MODE]);
        return value === "11" ? BLACK_AND_WHITE_ADDENDUM : undefined;
      }}
      renderCard={(combination, id, { disabled, renderTooltip }) => {
        const value = String(combination[ATTRIBUTES.TEXT_PRINT_MODE]);
        const baseName = getDisplayName(ATTRIBUTES.TEXT_PRINT_MODE, value);
        if (!baseName) return null;
        const ratio = INTERIOR_COLOR_RATIOS.get(value);
        const displayName = ratio ? `${baseName} (${ratio})` : baseName;
        return (
          <TextCard key={id} value={id} displayName={displayName} disabled={disabled} renderTooltip={renderTooltip} />
        );
      }}
      flexDirection="column"
    />
  );
}

export function BleedStep(props: StepProps) {
  return (
    <ChoiceStep
      {...props}
      restrictedValues="disable"
      renderCard={(combination, id, { disabled, renderTooltip }) => {
        const value = String(combination[ATTRIBUTES.PRODUCT_CLASS]);
        const displayName = getDisplayName(ATTRIBUTES.PRODUCT_CLASS, value);
        return displayName ? (
          <TextCard key={id} value={id} displayName={displayName} disabled={disabled} renderTooltip={renderTooltip} />
        ) : null;
      }}
      renderImage={(combination) => {
        const value = String(combination[ATTRIBUTES.PRODUCT_CLASS]);
        const displayName = getDisplayName(ATTRIBUTES.PRODUCT_CLASS, value);
        const image = getProductImage(ATTRIBUTES.PRODUCT_CLASS, value);
        return image && displayName ? <ProductImage displayName={displayName} image={image} /> : null;
      }}
      flexDirection="column"
    />
  );
}
